import { ChevronLeft, ChevronRight } from 'lucide-react';

/**
 * Paginación en cliente, reutilizable (LAE y SEPE).
 *
 * Props:
 *  - page            página actual (1-based)
 *  - pageSize        filas por página
 *  - total           nº total de filas (ya filtradas)
 *  - onPageChange    (n) => void
 *  - onPageSizeChange(n) => void   (opcional; si no se pasa, no muestra el selector)
 *  - pageSizeOptions opcional, por defecto [25, 50, 100]
 *
 * El estado (page/pageSize) vive en la pantalla; aquí solo se pinta y se avisa.
 */
export default function Paginacion({
  page, pageSize, total, onPageChange, onPageSizeChange, pageSizeOptions = [25, 50, 100],
}) {
  const totalPaginas = Math.max(1, Math.ceil(total / pageSize));
  const desde = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const hasta = Math.min(page * pageSize, total);

  // Ventana de páginas con elipsis: 1 … (page-1) page (page+1) … N
  const paginas = [];
  for (let p = 1; p <= totalPaginas; p += 1) {
    if (p === 1 || p === totalPaginas || (p >= page - 1 && p <= page + 1)) paginas.push(p);
    else if (paginas[paginas.length - 1] !== '…') paginas.push('…');
  }

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-2.5 border-t border-slate-100">
      <div className="text-xs text-slate-500">
        Mostrando <span className="font-medium text-slate-700">{desde}–{hasta}</span> de{' '}
        <span className="font-medium text-slate-700">{total}</span>
      </div>

      <div className="flex items-center gap-2">
        {onPageSizeChange && (
          <select
            className="border border-slate-300 rounded-md px-2 py-1 text-xs text-slate-600"
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
          >
            {pageSizeOptions.map((n) => <option key={n} value={n}>{n}/pág.</option>)}
          </select>
        )}

        <div className="flex items-center gap-1">
          <button
            type="button"
            className="p-1.5 rounded-md border border-slate-200 text-slate-500 hover:bg-slate-50 disabled:opacity-40 disabled:hover:bg-transparent"
            onClick={() => onPageChange(page - 1)}
            disabled={page <= 1}
            aria-label="Página anterior"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>

          {paginas.map((p, i) => (p === '…'
            ? <span key={`e-${i}`} className="px-1.5 text-slate-400 text-xs">…</span>
            : (
              <button
                key={p}
                type="button"
                onClick={() => onPageChange(p)}
                className={
                  'min-w-[28px] px-2 py-1 rounded-md text-xs border transition-colors ' +
                  (p === page
                    ? 'bg-slate-900 text-white border-slate-900'
                    : 'border-slate-200 text-slate-600 hover:bg-slate-50')
                }
              >
                {p}
              </button>
            )))}

          <button
            type="button"
            className="p-1.5 rounded-md border border-slate-200 text-slate-500 hover:bg-slate-50 disabled:opacity-40 disabled:hover:bg-transparent"
            onClick={() => onPageChange(page + 1)}
            disabled={page >= totalPaginas}
            aria-label="Página siguiente"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
